import { useState } from "react";
import { Copy, Gauge, Moon, Pause, Power, TriangleAlert, Wrench } from "lucide-react";
import {
  formatLocalClock,
  isGroomingQueueSetupCode,
  queuePauseBanner,
  type GroomingQueuePause,
  type GroomingQueueSetupCode,
  type LocalClockOptions,
} from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { useCopyFeedback } from "@/hooks/use-copy-feedback";
import { adminDetailsText } from "@/lib/grooming/copy-details";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { fixItForPause, fixItHint, pauseRepoHost, type FixIt, type FixItResult } from "@/lib/toolbox/fix-it";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * Why the run queue is paused, one banner per reason (epic PI-SPEC-SW-E284;
 * rewritten for epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1479, Claude Design
 * frames B2 and B3). Every sentence comes from the contract's wording sheet
 * in this page's own version (`queuePauseBanner`), chosen by `reason` and
 * `errorCode`, never from `pause.fix`: an older app sends its own command
 * texts there.
 * - `launch`: "5 items waiting from last time." · Resume;
 * - `user`: "You stopped the run. 5 items waiting." · Resume;
 * - `usage-limit`: "Paused before your Claude limit. Starts again by itself at
 *   about 2:30 PM." in the viewer's own time, with Resume to start sooner (or
 *   when the app sent no reset time, so it would never start by itself);
 * - `sleep`: "Paused while the computer slept." · Resume (a newer app starts
 *   again by itself on wake and then shows the picked-up line instead);
 * - `setup` (amber, frame B2): the code's sentence, a context line (when it
 *   stopped and what went back to the front), what Fix it does, exactly one
 *   Fix it and "Copy details for the Admin". No Resume: a successful Fix it
 *   starts the queue again by itself (01-roles.md section 9.6).
 */

/** The sentence for a pause that is not `sleep`, else the sleep sentence. Pure; exported for tests. */
export function pauseText(pause: GroomingQueuePause, left: number, clock?: LocalClockOptions): string {
  if (pause.reason === "sleep") return "Paused while the computer slept.";
  // A newer app may say the repository is on GitHub (E318); otherwise the contract's Azure DevOps words, unchanged.
  const github = pauseRepoHost(pause) === "github";
  return queuePauseBanner(pause, { left, ...(clock ? { clock } : {}), ...(github ? { host: "github" as const } : {}) });
}

/** What the context line says stopped, for each setup code. */
const SETUP_LEAD: Readonly<Partial<Record<GroomingQueueSetupCode, string>>> = {
  CLAUDE_NOT_SIGNED_IN: "Your Claude sign-in ran out",
  ADO_PAT_INVALID: "Azure DevOps stopped accepting your token",
  NETWORK: "The connection dropped",
  REPO_AUTH_REQUIRED: "Azure DevOps asked for your sign-in again",
};

/** The context line's lead for a GitHub repository's sign-in (E318). */
const GITHUB_REPO_AUTH_LEAD = "GitHub asked for your sign-in again";

function count(value: number | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;
}

/**
 * The setup banner's context line from `since` and `stoppedEntries`: "Your
 * Claude sign-in ran out at 4:12 PM, while 2 briefs were running. They are
 * back at the front of the queue; nothing is lost." Null when the app sent
 * neither (an older app). Pure; exported for tests.
 */
export function setupContextText(
  pause: Pick<GroomingQueuePause, "errorCode" | "since" | "stoppedEntries">,
  clock?: LocalClockOptions
): string | null {
  const when = pause.since ? formatLocalClock(pause.since, clock) : null;
  const stopped = count(pause.stoppedEntries);
  if (when === null && stopped === null) return null;
  const lead =
    (pause.errorCode === "REPO_AUTH_REQUIRED" && pauseRepoHost(pause) === "github"
      ? GITHUB_REPO_AUTH_LEAD
      : isGroomingQueueSetupCode(pause.errorCode)
        ? SETUP_LEAD[pause.errorCode]
        : undefined) ?? "The queue paused";
  const head = when ? `${lead} ${when}` : lead;
  if (stopped !== null && stopped > 0) {
    const running = stopped === 1 ? "1 brief was running" : `${String(stopped)} briefs were running`;
    const back = stopped === 1 ? "It is back at the front of the queue" : "They are back at the front of the queue";
    return `${head}, while ${running}. ${back}; nothing is lost.`;
  }
  return `${head}. Nothing is lost.`;
}

const ICONS = {
  launch: Power,
  sleep: Moon,
  setup: TriangleAlert,
  "usage-limit": Gauge,
  user: Pause,
} as const;

export interface QueuePauseBannerProps {
  pause: GroomingQueuePause;
  /** Entries still to do (running and waiting), for the launch and user sentences. */
  left: number;
  onResume: () => void;
  /** True while Resume waits for the app. */
  busy?: boolean;
  /** Setup pause: run the Fix it (repair, then resume); resolves with what happened. */
  onFixIt?: (fix: FixIt) => Promise<FixItResult>;
  /** For tests: "now" and the time zone for every time in the banner. */
  clock?: LocalClockOptions;
}

export function QueuePauseBanner({ pause, left, onResume, busy = false, onFixIt, clock }: QueuePauseBannerProps) {
  if (pause.reason === "setup") {
    // A new pause (another code, or a new start time) starts with no Fix it outcome under it.
    const identity = `${pause.errorCode ?? ""}|${pause.since ?? ""}`;
    return <SetupPauseBanner key={identity} pause={pause} onFixIt={onFixIt} clock={clock} />;
  }

  const Icon = ICONS[pause.reason];
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-[10px] border border-[#27272A] bg-[#111113] px-4 py-2.5 text-foreground"
      data-testid="queue-pause-banner"
      data-reason={pause.reason}
    >
      <Icon className={cn("h-4 w-4 flex-none", TOOLBOX_TEXT.faint)} aria-hidden="true" />
      <p className={cn("min-w-0 flex-1", TOOLBOX_TEXT.content)} data-testid="queue-pause-text">
        {pauseText(pause, left, clock)}
      </p>
      <Button
        type="button"
        size="sm"
        className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
        disabled={busy}
        onClick={onResume}
      >
        Resume
      </Button>
    </div>
  );
}

/** What the Fix it row shows after a press. */
type FixOutcome = { ok: boolean; text: string } | null;

/** Frame B2's amber bar for a `setup` pause. */
function SetupPauseBanner({
  pause,
  onFixIt,
  clock,
}: {
  pause: GroomingQueuePause;
  onFixIt: QueuePauseBannerProps["onFixIt"];
  clock: LocalClockOptions | undefined;
}) {
  const bridge = useToolboxBridge();
  const [fixing, setFixing] = useState(false);
  const [outcome, setOutcome] = useState<FixOutcome>(null);
  const { copied, copy } = useCopyFeedback();
  const fix = fixItForPause(pause);
  const sentence = pauseText(pause, 0, clock);
  const context = setupContextText(pause, clock);

  const runFix = () => {
    if (!fix || !onFixIt) return;
    setFixing(true);
    setOutcome(null);
    void onFixIt(fix).then((result) => {
      setFixing(false);
      if (result.kind === "resumed") setOutcome({ ok: true, text: result.message });
      else if (result.kind === "failed") setOutcome({ ok: false, text: result.message });
    });
  };

  const copyDetails = () => {
    const text = adminDetailsText({
      what: `Queue paused: ${sentence}`,
      errorCode: pause.errorCode,
      rawMessage: pause.detail ?? pause.fix,
      at: pause.since ?? new Date(),
      appVersion: bridge?.app.version,
      contracts: bridge?.app.contracts,
    });
    copy(text);
  };

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-[10px] border border-amber-500/30 bg-amber-500/[0.08] px-4 py-3"
      data-testid="queue-pause-banner"
      data-reason="setup"
      data-error-code={pause.errorCode}
    >
      <TriangleAlert className="mt-0.5 h-5 w-5 flex-none text-[#FBBF24]" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="text-sm font-semibold text-white" data-testid="queue-pause-text">
          {sentence}
        </p>
        <p className="text-[13px] leading-relaxed text-[#FCE7B2]" data-testid="queue-pause-context">
          {context ? `${context} ` : ""}
          {fix ? fixItHint(fix) : null}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {fix && onFixIt && (
            <Button
              type="button"
              size="sm"
              className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={fixing}
              onClick={runFix}
            >
              <Wrench className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              Fix it
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-[#FCE7B2] hover:bg-amber-500/10 hover:text-[#FCE7B2]"
            onClick={copyDetails}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {copied ? "Copied" : "Copy details for the Admin"}
          </Button>
        </div>
        {outcome && (
          <p
            className={cn("text-[13px]", outcome.ok ? "text-[#FCE7B2]" : "text-[#FF8A9B]")}
            role={outcome.ok ? "status" : "alert"}
            data-testid="queue-fix-outcome"
          >
            {outcome.text}
          </p>
        )}
      </div>
    </div>
  );
}
